import { useEffect, useRef, useState } from 'react';
import {
  getInstitutionalDocumentHistory,
  uploadInstitutionalActDocument,
  downloadInstitutionalDocumentVersion,
  type InstitutionalDocumentVersion,
  type InstitutionalActRecord,
  type ReviewOrganization,
} from '@urbanik/api-client';
import { Button, CanonicalModal, TextAreaField } from '@urbanik/ui';
import { reviewApiBase } from './api-base';
export function InstitutionalActDocuments({
  organization,
  act,
  onClose,
}: {
  organization: ReviewOrganization;
  act: InstitutionalActRecord;
  onClose: () => void;
}) {
  const [current, setCurrent] = useState(act),
    [versions, setVersions] = useState<readonly InstitutionalDocumentVersion[]>([]),
    [file, setFile] = useState<File | null>(null),
    [reason, setReason] = useState(''),
    [error, setError] = useState(''),
    [busy, setBusy] = useState(false),
    [loading, setLoading] = useState(true),
    [refresh, setRefresh] = useState(0),
    [success, setSuccess] = useState('');
  const pending = useRef<AbortController | null>(null),
    inFlight = useRef(false),
    fileInput = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setVersions([]);
    setError('');
    void getInstitutionalDocumentHistory(reviewApiBase, organization.id, act.id, controller.signal)
      .then((r) => {
        if (!controller.signal.aborted) setVersions(r.items);
      })
      .catch((e) => {
        if (!controller.signal.aborted)
          setError(e instanceof Error ? e.message : 'Error documental.');
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => {
      controller.abort();
      pending.current?.abort();
    };
  }, [organization.id, act.id, refresh]);
  async function save() {
    if (
      inFlight.current ||
      busy ||
      loading ||
      !canUpload ||
      !file ||
      reason.trim().length < 5 ||
      reason.trim().length > 500
    )
      return;
    inFlight.current = true;
    const controller = new AbortController();
    pending.current = controller;
    setBusy(true);
    setError('');
    setSuccess('');
    try {
      const result = await uploadInstitutionalActDocument(
        reviewApiBase,
        organization.id,
        current.id,
        file,
        current.version,
        reason.trim(),
        controller.signal,
      );
      if (controller.signal.aborted) return;
      setCurrent(result);
      setFile(null);
      setReason('');
      if (fileInput.current) fileInput.current.value = '';
      setSuccess('PDF guardado; las versiones anteriores se conservan.');
      setRefresh((v) => v + 1);
    } catch (e) {
      if (!controller.signal.aborted)
        setError(e instanceof Error ? e.message : 'No se pudo guardar el PDF.');
    } finally {
      inFlight.current = false;
      if (!controller.signal.aborted) {
        setBusy(false);
      }
    }
  }
  async function download(version: InstitutionalDocumentVersion) {
    const controller = new AbortController();
    pending.current = controller;
    setBusy(true);
    setError('');
    try {
      const blob = await downloadInstitutionalDocumentVersion(
        reviewApiBase,
        organization.id,
        act.id,
        version.id,
        controller.signal,
      );
      if (controller.signal.aborted) return;
      const url = URL.createObjectURL(blob),
        link = document.createElement('a');
      link.href = url;
      link.download = version.fileName;
      link.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      if (!controller.signal.aborted)
        setError(e instanceof Error ? e.message : 'No se pudo descargar.');
    } finally {
      if (!controller.signal.aborted) setBusy(false);
    }
  }
  const canUpload =
    organization.permissions.includes('governance.acts.document.upload') && !current.deletedAt;
  return (
    <CanonicalModal
      open
      title="Documentos del acta"
      description={organization.name + ' · ' + current.number}
      onClose={() => {
        if (!busy) onClose();
      }}
    >
      {error ? <p role="alert">{error}</p> : null}
      {success ? <p role="status">{success}</p> : null}
      <p>
        Selecciona el PDF e indica el motivo obligatorio antes de confirmar la carga o el reemplazo.
        Las versiones anteriores se conservan.
      </p>
      {canUpload ? (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            void save();
          }}
        >
          <label className="ui-field">
            PDF del acta (máximo 20 MB)
            <input
              ref={fileInput}
              type="file"
              accept=".pdf,application/pdf"
              disabled={busy || loading}
              onChange={(e) => {
                setFile(e.target.files?.[0] ?? null);
              }}
            />
          </label>
          <TextAreaField
            label="Motivo de carga o reemplazo"
            required
            minLength={5}
            maxLength={500}
            value={reason}
            disabled={busy || loading}
            onChange={(event) => setReason(event.target.value)}
          />
          <p>Indica el motivo (entre 5 y 500 caracteres). Quedará registrado en el historial.</p>
          <Button
            type="submit"
            disabled={
              busy || loading || !file || reason.trim().length < 5 || reason.trim().length > 500
            }
          >
            {busy ? 'Procesando…' : current.hasDocument ? 'Reemplazar PDF' : 'Cargar PDF'}
          </Button>
        </form>
      ) : (
        <p>Solo consulta documental: sin permiso de carga o acta en papelera.</p>
      )}
      <h3>Versiones conservadas</h3>
      {loading ? (
        <p role="status">Cargando versiones…</p>
      ) : versions.length ? (
        <ol>
          {versions.map((version) => (
            <li key={version.id}>
              <strong>
                Versión {version.version} · {version.fileName}
              </strong>
              <p>
                {version.origin === 'preserved'
                  ? 'Documento anterior conservado'
                  : 'Carga registrada'}{' '}
                · {version.uploadedAt} · {version.sizeBytes} bytes
              </p>
              <p>{version.reason}</p>
              <Button disabled={busy} onClick={() => void download(version)}>
                Descargar versión {version.version}
              </Button>
            </li>
          ))}
        </ol>
      ) : (
        <p>
          {current.hasDocument
            ? 'El PDF actual se conservará como versión al realizar su primer reemplazo.'
            : 'Aún no hay versiones documentales.'}
        </p>
      )}
      <Button disabled={busy} onClick={onClose}>
        Cerrar documentos
      </Button>
    </CanonicalModal>
  );
}
